import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple } from "@phosphor-icons/react";
import SearchBar, { matchesQuery } from "@/components/gemora/SearchBar";
import AsyncButton from "@/components/gemora/AsyncButton";

const STATUSES = ["open", "in_progress", "resolved", "closed"];

export default function AdminQueries() {
  const [queries, setQueries] = useState([]);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = () => api.get("/admin/queries", { params: filter ? { status: filter } : {} }).then((r) => setQueries(r.data));
  useEffect(() => { refresh(); }, [filter]);

  const shown = queries.filter((q) => matchesQuery(query, [q.subject, q.name, q.email, q.phone, q.message, q.category, q.order_no]));

  const update = async (id, patch) => {
    setSaving(true);
    try {
      await api.patch(`/admin/queries/${id}`, patch);
      toast.success("Saved"); setNote(""); setOpenId(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Support inbox</div>
      <h1 className="font-display text-4xl text-ink mt-1 mb-6">User queries</h1>
      <SearchBar value={query} onChange={setQuery} placeholder="Search queries by subject, name, email or message…" testId="queries-search" className="mb-4 max-w-md" />
      <div className="flex gap-2 mb-4 flex-wrap">
        {["", ...STATUSES].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 border ${filter === s ? "bg-maroon text-ivory border-maroon" : "border-gold/40 text-ink-soft hover:border-maroon"}`}>
            {s || "All"}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {shown.length === 0 && <div className="gold-line p-10 text-center text-ink-muted">{query ? `No queries match “${query}”.` : "No queries."}</div>}
        {shown.map((q) => (
          <div key={q.query_id} className="gold-line bg-ivory p-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-serifd text-lg">{q.subject}</div>
                  {q.category && <span className="text-[10px] uppercase tracking-widest bg-cream border border-gold/40 text-maroon px-2 py-0.5">{q.category}</span>}
                  {q.order_no && <span className="text-[10px] font-mono text-ink-muted border border-gold/30 px-2 py-0.5">{q.order_no}</span>}
                </div>
                <div className="text-xs text-ink-muted">{q.name} · {q.email}{q.phone ? ` · ${q.phone}` : ""} · <span className="font-mono">{new Date(q.created_at).toLocaleString()}</span></div>
                <p className="mt-2 text-sm text-ink-soft">{q.message}</p>
              </div>
              <select value={q.status} onChange={(e) => update(q.query_id, { status: e.target.value })} className="gold-line text-xs bg-ivory px-2 py-1 uppercase tracking-widest">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {(q.notes && q.notes.length > 0) && (
              <div className="mt-3 border-t border-gold/20 pt-3 space-y-2">
                {q.notes.map((n, i) => (
                  <div key={i} className="text-xs text-ink-soft"><span className="font-mono text-ink-muted">{new Date(n.at).toLocaleString()}</span> · {n.text}</div>
                ))}
              </div>
            )}
            <button onClick={() => setOpenId(openId === q.query_id ? null : q.query_id)} className="mt-2 text-xs text-maroon underline">
              {openId === q.query_id ? "Cancel" : "Add note"}
            </button>
            {openId === q.query_id && (
              <div className="mt-2 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note or resolution…" className="flex-1 gold-line px-3 py-2 text-sm" />
                <AsyncButton onClick={() => update(q.query_id, { note })} loading={saving} loadingText="Saving…" className="brand-gradient text-ivory text-xs px-3">Save</AsyncButton>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
